/**
 * fluencyPracticeFlag — the ONE cross-app read/write path for
 * `lwa_fluency_practice_v1`, the parent-enabled "Smooth Reading" visibility flag
 * (repeated-reading FLUENCY practice: model → read-again ×2-3 → celebrate, on the
 * child's own decodable level, for BOTH the Foundations and the grade paths).
 *
 * WHY here in ui-shared (same rationale as extrasFlag.ts): the parent portal
 * (web-app) and mobile toggle this flag but CANNOT import web-child (`@child`) at
 * runtime, where the signed-envelope writer (`flagGuard.setRigorFlag`) lives. So the
 * reader AND a parent-gated writer live here, over the shared `createSignedFlag`
 * primitive (signedFlag.ts) that replicates flagGuard.ts's scheme byte-for-byte — so
 * an envelope written here is accepted by flagGuard's `readGuardedFlag` (and
 * vice-versa), and the ops hook
 * `window.__lwaRigorFlags.set('lwa_fluency_practice_v1', …)` stays interoperable.
 * `lwa_fluency_practice_v1` is registered in web-child/flagGuard.ts GUARDED_FLAGS
 * (it is DEFAULT-ON — see below — so it is NOT in DEFAULT_OFF_FLAGS).
 *
 * DEFAULT ON (owner 2026-09-10): an absent key reads TRUE — Smooth Reading closes
 * a real fluency gap, so every family gets it without a parent hunting for a toggle.
 * A bare / legacy / foreign localStorage write ('off', '0', …) is IGNORED; only a
 * correctly PARENT-signed `{ v:'off' }` envelope turns it OFF, so a child (or a
 * stray script) can move it neither way. Parents opt OUT in Settings. Never throws.
 *
 * DYSLEXIA-SAFE: the gated feature NEVER shows the child a clock or a WPM number —
 * it is automaticity through repetition. Rate/reps are tracked internally only (for
 * the parent report + the dormant `flu` assessment domain).
 */

import { createSignedFlag } from './signedFlag';

export const FLUENCY_PRACTICE_FLAG = 'lwa_fluency_practice_v1';

// DEFAULT ON (owner 2026-09-10): Smooth Reading closes a real fluency gap in the
// app, so every family gets it by default — a parent should not have to hunt for a
// learning-critical feature. The signed-envelope model inverts cleanly for a
// default-ON flag: an absent/bare/forged value reads ON (the default), and only a
// correctly PARENT-signed envelope can turn it OFF — a child or stray script can
// move it neither way. Parents opt OUT in Settings; nobody has to opt in.
const flag = createSignedFlag(FLUENCY_PRACTICE_FLAG, { defaultOn: true });

/**
 * True when a PARENT is signed in on this device: a structurally valid, unexpired
 * `auth_token` JWT (the parent-portal session — the same identity flagGuard
 * .hasParentGate rides on). The child token (`child_auth_token`) does NOT qualify.
 */
export function hasFluencyPracticeParentGate(): boolean {
  return flag.hasParentGate();
}

/**
 * Read the Smooth Reading flag. Default ON. Returns false ONLY for a correctly
 * parent-signed `{ v:'off', sig }` envelope; every other shape (absent, bare
 * 'off'/'0', malformed JSON, wrong/missing signature) reads true. Never throws.
 */
export function isFluencyPracticeEnabled(): boolean {
  return flag.isEnabled();
}

/**
 * Parent-gated write. Turning it ON (back to the default) clears the key; turning it
 * OFF stores a signed `{ v:'off', sig }` envelope. Refuses (returns false, writes
 * nothing) without a live parent session — so a child-side or stray write can never
 * turn the feature off.
 */
export function setFluencyPracticeEnabled(enabled: boolean): boolean {
  return flag.setEnabled(enabled);
}

/** TEST-ONLY seam: sign the flag ON without the parent-JWT check, so unit tests can
 *  exercise the enabled paths (mirrors flagGuard's __setGuardedFlagOnForTests). */
export function __setFluencyPracticeOnForTests(): void {
  flag.__setOnForTests();
}
